import { useState, useEffect } from 'react';
import { ClipboardList, Clock, ArrowRight, Printer, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);
const statusLabels = { pending: 'Pendiente', approved: 'Aprobado', production: 'En Produccion', delivery: 'En Entrega', delivered: 'Entregado' };
const statusColors = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-blue-100 text-blue-800', production: 'bg-purple-100 text-purple-800', delivery: 'bg-orange-100 text-orange-800', delivered: 'bg-green-100 text-green-800' };

const getColombiaToday = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};

const formatDateString = (dateStr) => {
  if (!dateStr) return '';
  const onlyDate = dateStr.split('T')[0];
  const parts = onlyDate.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

export default function AdminProduction() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    return getColombiaToday();
  });
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [selectedOrderForNovelty, setSelectedOrderForNovelty] = useState(null);
  const [noveltyText, setNoveltyText] = useState('');
  const [savingNovelty, setSavingNovelty] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const [approved, production, productsRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { withCredentials: true, headers: getHeaders(), params: { status: 'approved' } }),
        axios.get(`${API}/admin/orders`, { withCredentials: true, headers: getHeaders(), params: { status: 'production' } }),
        axios.get(`${API}/products`),
      ]);
      setOrders([...production.data, ...approved.data]);
      setProducts(productsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`Estado actualizado a ${statusLabels[newStatus]}`);
      fetchOrders();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const getOrderProductionDate = (order) => {
    if (order.production_date) return order.production_date.split('T')[0];
    if (order.delivery_date) {
      // Check if order contains only refrigerated products
      const isRefrigeratedOnly = order.items && order.items.length > 0 && order.items.every(item => {
        const prod = products.find(p => p.id === item.productId);
        const cat = (prod?.category || item.category || '').toLowerCase();
        return cat === 'refrigeradas' || cat === 'refrigeradas-premium';
      });
      // Refrigerated cakes without explicit production date are not scheduled automatically
      if (isRefrigeratedOnly) return '';
      return order.delivery_date.split('T')[0];
    }
    return '';
  };

  const filteredOrders = orders
    .filter(o => {
      const prodDate = getOrderProductionDate(o);
      if (!prodDate) return false;
      if (selectedDate) {
        return prodDate === selectedDate;
      }
      // "Ver Todos": solo pedidos del día actual y futuros para control de lo que falta por producir
      const todayStr = getColombiaToday();
      return prodDate >= todayStr;
    })
    .sort((a, b) => {
      const dateA = getOrderProductionDate(a);
      const dateB = getOrderProductionDate(b);
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.delivery_time_slot || '').localeCompare(b.delivery_time_slot || '');
    });

  const handleAddNovelty = async (e) => {
    e.preventDefault();
    if (!selectedOrderForNovelty || !noveltyText.trim()) return;
    setSavingNovelty(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${selectedOrderForNovelty.id}/novelties`, { text: noveltyText }, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      toast.success('Novedad registrada con éxito');
      const updatedNovelties = [...(selectedOrderForNovelty.novelties || []), data.novelty];
      setSelectedOrderForNovelty(prev => ({ ...prev, novelties: updatedNovelties }));
      setOrders(prev => prev.map(o => o.id === selectedOrderForNovelty.id ? { ...o, novelties: updatedNovelties } : o));
      if (selectedOrderDetails?.id === selectedOrderForNovelty.id) {
        setSelectedOrderDetails(prev => ({ ...prev, novelties: updatedNovelties }));
      }
      setNoveltyText('');
    } catch {
      toast.error('Error al agregar la novedad');
    } finally {
      setSavingNovelty(false);
    }
  };

  const approvedOrders = filteredOrders.filter(o => o.status === 'approved');
  const productionOrders = filteredOrders.filter(o => o.status === 'production');

  const renderOrderCard = (order) => {
    const isApproved = order.status === 'approved';
    const cardBorder = isApproved 
      ? 'border-blue-200 bg-blue-50/30' 
      : 'border-purple-200 bg-purple-50/30';
    const prodDate = getOrderProductionDate(order);
    const todayStr = getColombiaToday();
    const isToday = prodDate === todayStr;

    return (
      <Card key={order.id} className={`p-5 border rounded-2xl flex flex-col justify-between ${cardBorder}`} data-testid={`production-order-${order.tracking_code}`}>
        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-body text-sm font-semibold text-[#46371c]">#{order.tracking_code}</p>
                {isToday ? (
                  <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold">
                    Hoy
                  </Badge>
                ) : prodDate > todayStr ? (
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">
                    Futuro ({formatDateString(prodDate)})
                  </Badge>
                ) : null}
              </div>
              <p className="font-body text-xs text-[#75664d]">{order.customer_name} ({order.customer_phone})</p>
              <p className="font-body text-[10px] text-[#75664d] mt-0.5">
                Creado: <span className="font-semibold text-[#46371c]">{order.created_at ? new Date(order.created_at).toLocaleString('es-CO') : 'N/A'}</span>
              </p>
              <p className="font-body text-[10px] text-[#75664d] mt-0.5">
                Montado por: <span className="font-semibold text-[#46371c]">{order.created_by || 'Cliente'}</span>
                {order.accepted_by && (
                  <span className="ml-1 text-blue-700 font-semibold">• Aceptado: {order.accepted_by}</span>
                )}
              </p>
              <p className="font-body text-[10px] text-[#e05275] font-semibold mt-0.5">
                Entrega: {order.delivery_date ? formatDateString(order.delivery_date) : 'No asignada'} {order.delivery_time_slot ? `(${order.delivery_time_slot})` : ''}
              </p>
              {order.production_date && (
                <p className="font-body text-[10px] text-purple-700 font-semibold mt-0.5">
                  Fecha Prod: <span className="font-bold">{formatDateString(order.production_date)}</span>
                </p>
              )}
            </div>
            <Badge className={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
          </div>

          {order.novelties && order.novelties.length > 0 && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
              <div className="flex items-center justify-between font-semibold text-[10px] text-amber-800 mb-0.5">
                <span>📌 Novedades ({order.novelties.length}):</span>
                <span className="text-[9px] text-amber-600">{order.novelties[order.novelties.length - 1]?.date?.split('T')[0]}</span>
              </div>
              <p className="text-[11px] italic font-medium line-clamp-2">
                "{order.novelties[order.novelties.length - 1]?.text}"
              </p>
              <span className="text-[9px] text-amber-700 block mt-0.5 font-semibold">
                — {order.novelties[order.novelties.length - 1]?.author}
              </span>
            </div>
          )}

          <div className="space-y-3 mb-4 bg-white p-3 rounded-xl border border-[#e5dfd3]/50">
            {order.items?.map((item, i) => {
              const productInfo = products.find(p => p.id === item.productId);
              const imageUrls = (item.image || productInfo?.image || (item.productId === 'gift-promo' ? '/obsequio.webp' : "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=150")).split(',');
              return (
                <div key={i} className="flex gap-3 text-xs font-body">
                  <div className="flex gap-1 flex-wrap shrink-0">
                    {imageUrls.map((url, imgIdx) => (
                      <img 
                        key={imgIdx}
                        src={url} 
                        alt={item.name} 
                        onClick={() => setZoomedImage(url)}
                        className="w-12 h-12 object-cover rounded-lg border border-[#e5dfd3] shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity" 
                      />
                    ))}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between gap-1.5">
                      <p className="font-bold text-[#46371c] leading-tight text-xs sm:text-sm">{item.name}</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold bg-[#46371c] text-white shadow-sm shrink-0">
                        Cant: {item.quantity || 1}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-1 gap-y-0.5 text-[10px] text-[#75664d] mt-1">
                      <span><strong>Porc:</strong> {item.size || 'Estándar'}</span>
                      <span><strong>Sabor:</strong> {item.flavor || 'N/A'}</span>
                      <span><strong>Rell:</strong> {item.filling || 'N/A'}</span>
                      <span><strong>Cantidad:</strong> <strong className="text-[#46371c] font-bold text-xs">{item.quantity || 1} {item.quantity > 1 ? 'uds' : 'ud'}</strong></span>
                      {item.complements && item.complements.length > 0 && (
                        <span className="col-span-2"><strong>Compl:</strong> {item.complements.join(', ')}</span>
                      )}
                    </div>
                    {item.observations && (
                      <p className="text-[10px] font-medium text-[#e05275] bg-[#e05275]/5 p-1 rounded mt-1">
                        <strong>Instrucciones:</strong> {item.observations}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-xs font-body text-[#75664d] space-y-0.5 mb-4 border-t border-[#e5dfd3]/20 pt-2">
            <p><strong>Tipo:</strong> {order.delivery_type === 'delivery' ? 'Domicilio' : 'Retiro en Tienda'}</p>
            {order.delivery_type === 'delivery' ? (
              <p><strong>Dirección:</strong> {order.delivery_address}</p>
            ) : (
              <p><strong>Sede:</strong> {order.store_name}</p>
            )}
            {order.observations && (
              <p className="italic bg-[#f4f1ea] p-2 rounded-lg mt-1 text-[#46371c]">Obs: {order.observations}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[#e5dfd3]/20 pt-3 flex-wrap">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedOrderDetails(order)}
              className="rounded-full font-body text-xs text-[#46371c] border-[#e5dfd3]"
            >
              Ver Detalles
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSelectedOrderForNovelty(order); setNoveltyText(''); }}
              className="rounded-full font-body text-xs border-amber-300 bg-amber-50/70 text-amber-900 hover:bg-amber-100"
            >
              📌 Novedad {order.novelties?.length ? `(${order.novelties.length})` : ''}
            </Button>
          </div>
          {isApproved ? (
            <Button 
              onClick={() => updateStatus(order.id, 'production')} 
              size="sm" 
              className="bg-purple-600 text-white hover:bg-purple-700 rounded-full font-body text-xs" 
              data-testid={`start-production-${order.tracking_code}`}
            >
              Iniciar Producción <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={() => updateStatus(order.id, 'delivery')} 
              size="sm" 
              className="bg-green-600 text-white hover:bg-green-700 rounded-full font-body text-xs font-semibold" 
              data-testid={`ready-for-delivery-${order.tracking_code}`}
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Listo para Entrega
            </Button>
          )}
        </div>
      </Card>
    );
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    const ordersListHtml = filteredOrders.map(order => 
      order.items?.map(item => `
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 8px;"><strong>${order.tracking_code}</strong></td>
          <td style="padding: 8px;">${order.created_at ? new Date(order.created_at).toLocaleString('es-CO') : 'N/A'}</td>
          <td style="padding: 8px;"><strong>${item.name}</strong></td>
          <td style="padding: 8px; text-align: center; font-weight: bold; font-size: 14px; background: #faf8f5;">${item.quantity || 1}</td>
          <td style="padding: 8px;">${item.size || 'Estándar'}</td>
          <td style="padding: 8px;">${item.flavor || 'N/A'}</td>
          <td style="padding: 8px;">${item.filling || 'N/A'}</td>
          <td style="padding: 8px;">${item.complements?.join(', ') || 'Ninguno'}</td>
          <td style="padding: 8px; color: #e05275;"><strong>${item.observations || 'Sin observaciones'}</strong></td>
          <td style="padding: 8px;">${order.delivery_type === 'delivery' ? 'Domicilio: ' + order.delivery_address : 'Retiro en: ' + order.store_name}</td>
        </tr>
      `).join('')
    ).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Informe de Produccion - ${selectedDate || 'Todas las fechas'}</title>
          <style>
            body { font-family: sans-serif; margin: 20px; color: #333; }
            h1 { font-size: 20px; margin-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th { background-color: #f2f2f2; text-align: left; padding: 8px; border-bottom: 2px solid #ddd; }
            td { font-size: 13px; padding: 8px; border-bottom: 1px solid #eee; }
          </style>
        </head>
        <body>
          <h1>Informe Diario de Produccion</h1>
          <p><strong>Fecha de entrega:</strong> ${selectedDate || 'Todas las fechas'}</p>
          <p><strong>Generado el:</strong> ${new Date().toLocaleDateString('es-CO')} ${new Date().toLocaleTimeString('es-CO')}</p>
          <table>
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Fecha Creación</th>
                <th>Producto</th>
                <th style="text-align: center;">Cantidad</th>
                <th>Tamaño/Porciones</th>
                <th>Sabor</th>
                <th>Relleno</th>
                <th>Complementos</th>
                <th>Instrucciones Especiales</th>
                <th>Entrega</th>
              </tr>
            </thead>
            <tbody>
              ${ordersListHtml || '<tr><td colspan="10" style="text-align:center; padding: 20px;">No hay pedidos para esta fecha</td></tr>'}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div data-testid="admin-production-page" className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#46371c]">Producción</h1>
          <p className="font-body text-xs sm:text-sm text-[#75664d] mt-1">
            {selectedDate 
              ? `Pedidos en producción para el día ${formatDateString(selectedDate)}` 
              : 'Pedidos por producir de hoy y futuras fechas (sin pedidos pasados)'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="font-body text-xs text-[#75664d] whitespace-nowrap">Fecha:</span>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="h-9 sm:h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs sm:text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] flex-1 sm:flex-none"
            />
            {selectedDate !== getColombiaToday() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(getColombiaToday())}
                className="h-9 sm:h-10 rounded-xl text-xs font-body text-[#46371c] border-[#e5dfd3] shrink-0"
              >
                Hoy
              </Button>
            )}
            {selectedDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate('')}
                className="h-9 sm:h-10 rounded-xl text-xs font-body text-[#46371c] border-[#e5dfd3] shrink-0"
              >
                Ver Todos (Hoy y Futuros)
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start mt-1 sm:mt-0">
            <Button
              onClick={printReport}
              className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body h-9 sm:h-10 px-4 text-xs sm:text-sm flex-1 sm:flex-none"
              disabled={filteredOrders.length === 0}
            >
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
            <Badge className="bg-purple-100 text-purple-800 text-xs sm:text-sm py-1.5 px-3 rounded-xl">
              {filteredOrders.length} pedidos
            </Badge>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-8">
          {filteredOrders.length === 0 ? (
            <Card className="p-12 text-center border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
              <ClipboardList className="w-12 h-12 mx-auto text-[#e5dfd3] mb-4" />
              <p className="font-body text-[#75664d]">
                {selectedDate 
                  ? `No hay pedidos programados para el día ${formatDateString(selectedDate)}` 
                  : 'No hay pedidos en producción'}
              </p>
            </Card>
          ) : (
            <>
              {approvedOrders.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-[#46371c] mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" /> Por iniciar ({approvedOrders.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {approvedOrders.map(order => renderOrderCard(order))}
                  </div>
                </div>
              )}

              {productionOrders.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-[#46371c] mb-4 flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-purple-600" /> En Producción ({productionOrders.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {productionOrders.map(order => renderOrderCard(order))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Dialog for Order Details */}
      {/* Dialog for Order Details */}
      <Dialog open={!!selectedOrderDetails} onOpenChange={(open) => !open && setSelectedOrderDetails(null)}>
        <DialogContent className="max-w-2xl bg-[#fcfbf9] max-h-[90vh] overflow-y-auto">
          {selectedOrderDetails && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#46371c]">
                  Detalles del Pedido #{selectedOrderDetails.tracking_code}
                </DialogTitle>
                <DialogDescription className="font-body text-sm text-[#75664d]">
                  Información completa del pedido para producción
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Información del Cliente y Entrega */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-[#f4f1ea] p-4 rounded-xl">
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-[#46371c] mb-2 border-b border-[#e5dfd3]/50 pb-1">Cliente</h4>
                    <p className="font-body text-xs text-[#46371c]"><strong className="text-[#75664d]">Fecha de Creación:</strong> <span className="font-semibold">{selectedOrderDetails.created_at ? new Date(selectedOrderDetails.created_at).toLocaleString('es-CO') : 'N/A'}</span></p>
                    <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Nombre:</strong> {selectedOrderDetails.customer_name}</p>
                    <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Teléfono:</strong> {selectedOrderDetails.customer_phone}</p>
                    <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Email:</strong> {selectedOrderDetails.customer_email || 'No proporcionado'}</p>
                    <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Montado por:</strong> <span className="font-semibold">{selectedOrderDetails.created_by || 'Cliente'}</span></p>
                    {selectedOrderDetails.accepted_by && (
                      <p className="font-body text-xs text-blue-800 mt-1"><strong className="text-blue-900">Aceptado por:</strong> <span className="font-semibold">{selectedOrderDetails.accepted_by}</span></p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-[#46371c] mb-2 border-b border-[#e5dfd3]/50 pb-1">Entrega</h4>
                    <p className="font-body text-xs text-[#46371c]"><strong className="text-[#75664d]">Tipo:</strong> {selectedOrderDetails.delivery_type === 'delivery' ? 'Domicilio' : 'Retiro en tienda'}</p>
                    {selectedOrderDetails.store_name && <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Sede:</strong> {selectedOrderDetails.store_name}</p>}
                    {selectedOrderDetails.delivery_address && <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Dirección:</strong> {selectedOrderDetails.delivery_address}</p>}
                     <p className="font-body text-xs text-[#46371c] mt-1"><strong className="text-[#75664d]">Fecha de Entrega:</strong> {selectedOrderDetails.delivery_date ? formatDateString(selectedOrderDetails.delivery_date) : 'No especificada'}</p>
                  </div>
                </div>

                {/* Observaciones generales */}
                {selectedOrderDetails.observations && (
                  <div className="bg-[#f0eadd]/50 p-3 rounded-xl border border-[#e5dfd3]/60">
                    <h4 className="font-heading text-xs font-semibold text-[#46371c] mb-1">Observaciones del Pedido:</h4>
                    <p className="font-body text-xs text-[#75664d] italic whitespace-pre-wrap">{selectedOrderDetails.observations}</p>
                  </div>
                )}

                {/* Items del Pedido */}
                <div>
                  <h4 className="font-heading text-sm font-semibold text-[#46371c] mb-3">Productos ({selectedOrderDetails.items?.length})</h4>
                  <div className="space-y-4">
                    {selectedOrderDetails.items?.map((item, idx) => {
                      const productInfo = products.find(p => p.id === item.productId);
                      const imageUrls = (item.image || productInfo?.image || (item.productId === 'gift-promo' ? '/obsequio.webp' : "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=300")).split(',');

                      return (
                        <div key={idx} className="flex gap-4 border border-[#e5dfd3] p-3 rounded-xl bg-white">
                          <div className="flex gap-1.5 flex-wrap shrink-0">
                            {imageUrls.map((url, imgIdx) => (
                              <img
                                key={imgIdx}
                                src={url}
                                alt={item.name}
                                onClick={() => setZoomedImage(url)}
                                className="w-20 h-20 object-cover rounded-lg border border-[#e5dfd3] shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity"
                              />
                            ))}
                          </div>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-body text-sm font-bold text-[#46371c]">{item.name}</p>
                              <Badge className="bg-[#46371c] text-white font-bold text-xs px-2.5 py-0.5 rounded-lg shadow-sm">
                                Cantidad: {item.quantity || 1}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-0.5 text-xs font-body">
                              <p className="text-[#75664d]"><strong className="text-[#46371c]">Porciones/Tamaño:</strong> {item.size || 'Estándar'}</p>
                              <p className="text-[#75664d]"><strong className="text-[#46371c]">Sabor:</strong> {item.flavor || 'No especificado'}</p>
                              <p className="text-[#75664d]"><strong className="text-[#46371c]">Relleno:</strong> {item.filling || 'No especificado'}</p>
                              {item.complements && item.complements.length > 0 && (
                                <p className="text-[#75664d]"><strong className="text-[#46371c]">Complementos:</strong> {item.complements.join(', ')}</p>
                              )}
                            </div>
                            {item.observations && (
                              <p className="font-body text-xs text-[#e05275] bg-[#e05275]/5 p-2 rounded mt-1">
                                <strong>Instrucciones:</strong> {item.observations}
                              </p>
                            )}
                            <div className="flex justify-between items-center pt-1 border-t border-[#e5dfd3]/20 mt-1">
                              <span className="font-body text-xs font-semibold text-[#46371c] bg-[#a0d5ba]/20 px-2 py-0.5 rounded-md border border-[#a0d5ba]">
                                Cantidad a Producir: {item.quantity || 1}
                              </span>
                              <span className="font-body text-xs font-semibold text-[#46371c]">{formatPrice(item.subtotal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#e5dfd3]/40 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedOrderForNovelty(selectedOrderDetails);
                    setNoveltyText('');
                  }}
                  className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 rounded-full font-body text-xs"
                >
                  📌 Gestionar Novedades ({selectedOrderDetails?.novelties?.length || 0})
                </Button>
                <Button onClick={() => setSelectedOrderDetails(null)} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body text-xs px-5">
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Novelties Dialog */}
      <Dialog open={!!selectedOrderForNovelty} onOpenChange={(open) => !open && setSelectedOrderForNovelty(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-md bg-[#fcfbf9] rounded-2xl border border-[#e5dfd3] max-h-[90vh] overflow-y-auto">
          {selectedOrderForNovelty && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg text-[#46371c]">
                  📌 Novedades del Pedido #{selectedOrderForNovelty.tracking_code}
                </DialogTitle>
                <DialogDescription className="font-body text-xs text-[#75664d]">
                  Registra y consulta notas importantes para producción y entrega.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <form onSubmit={handleAddNovelty} className="space-y-2.5 bg-white p-3.5 border border-[#e5dfd3] rounded-xl">
                  <label className="font-body text-xs font-semibold text-[#46371c] block">
                    Nueva Novedad
                  </label>
                  <textarea
                    value={noveltyText}
                    onChange={e => setNoveltyText(e.target.value)}
                    placeholder="Ej: Cliente solicita cambiar cobertura, pedido urgente..."
                    className="w-full p-2.5 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none h-20"
                    required
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={savingNovelty}
                      className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs h-8 px-4"
                    >
                      {savingNovelty ? 'Guardando...' : 'Guardar Novedad'}
                    </Button>
                  </div>
                </form>

                <div className="space-y-2">
                  <h4 className="font-heading text-xs font-bold text-[#46371c]">
                    Historial de Novedades ({selectedOrderForNovelty?.novelties?.length || 0})
                  </h4>
                  {selectedOrderForNovelty?.novelties && selectedOrderForNovelty.novelties.length > 0 ? (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {selectedOrderForNovelty.novelties.map((nov, idx) => (
                        <div key={idx} className="bg-amber-50/70 border border-amber-200/80 p-2.5 rounded-xl text-xs space-y-1">
                          <p className="font-body text-[#46371c] font-medium whitespace-pre-wrap">{nov.text}</p>
                          <div className="flex justify-between text-[10px] text-amber-900 font-semibold border-t border-amber-200/40 pt-1">
                            <span>{nov.author || 'Usuario'}</span>
                            <span>{nov.date ? new Date(nov.date).toLocaleString('es-CO') : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center border border-dashed border-[#e5dfd3] rounded-xl text-xs text-[#75664d]">
                      No hay novedades registradas para este pedido.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-3 border-t border-[#e5dfd3]/40 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedOrderForNovelty(null)}
                  className="rounded-full text-xs font-body text-[#46371c] border-[#e5dfd3]"
                >
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Zoom Image Dialog */}
      <Dialog open={!!zoomedImage} onOpenChange={() => setZoomedImage(null)}>
        <DialogContent className="max-w-2xl bg-white p-3 rounded-2xl border border-[#e5dfd3] flex flex-col items-center">
          <DialogHeader className="w-full text-center border-b border-[#e5dfd3]/50 pb-2 mb-2">
            <DialogTitle className="font-heading text-base font-bold text-[#46371c]">Detalle de Imagen</DialogTitle>
          </DialogHeader>
          {zoomedImage && (
            <img 
              src={zoomedImage} 
              alt="Zoomed Reference" 
              className="max-h-[70vh] max-w-full object-contain rounded-xl border border-[#e5dfd3]"
            />
          )}
          <div className="w-full flex justify-end mt-4">
            <Button onClick={() => setZoomedImage(null)} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body text-xs">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
